/**
 * SAP CONFIDENTIAL 
 *
 * This software is confidential proprietary software licensed by SAP SE 
 * and is not to be used, duplicated or disclosed to anyone without the 
 * prior written permission of SAP. 
 *
 * (C) 2020 Copyright SAP SE.
 */
import { Component, OnInit, Output, OnChanges, Input, EventEmitter, ViewEncapsulation, ViewChild, SimpleChanges, AfterViewInit } from '@angular/core';
import { CommonService } from '../../shared/services/common.service';
import { NgForm } from '@angular/forms';
import { COMPANY_CODES } from '../../config/constants';
import moment from 'moment';
import { PlanPeriodsService } from '../../shared/services/planperiods.service';
import { ChangeSponsorService } from '../changesponsor/change-sponsor.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ChangeOrganizationConfimationModalComponent } from "./change-organization-confirmation-modal.component";
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';

@Component({
  selector: 'app-change-organization',
  templateUrl: './change-organization.component.html',
  styleUrls: ['./change-organization.component.scss'],
  providers: [ChangeSponsorService],
  encapsulation: ViewEncapsulation.None
})
export class ChangeOrganizationComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('orgUnitForm') orgUnitForm!: NgForm;
  @Output() closeSlider: EventEmitter<any> = new EventEmitter();
  @Input() organization: any;

  public titleMaxLimit: boolean = false;
  private currentDate: Date = new Date();
  private globalDateFormatVar: any = localStorage.getItem('globalDateFormat');
  public datePickerOptions: Partial<BsDatepickerConfig> = {
    dateInputFormat: this.globalDateFormatVar
      ? this.globalDateFormatVar.toUpperCase()
      : 'MMM DD, YYYY',
      adaptivePosition: false,
      showTodayButton: true,
      todayPosition: 'center'
  };
  public isChangeOrgUnitAccess = true;
  public hasRequiredError = false;
  public isFormSubmitted = false;
  public isOrgUnitChanged = false;
  public defaultSearchText = '';
  public orgUnitPayload: any = {};
  public periodDetails: any;
  public startPeriodDateInMillis: any;
  public companyCodes: any;
  public companyCode: any;
  public currentOrganization: any;
  public isCompanyConfigNone = false;
  public organizationData: any = {};
  public newOrganization: any;
  public organizationSame = false;
  public fromName: any = {};
  public changeOrgUnitObj: any = {};
  public menuObj: any = {};
  public hasChangeAccess = true;
  public showDownlineLeg = false;
  public orgUnitIndex!: number;
  public periodStartDateInMillis: any;
  public validationObj = {
    userMessage: '',
    state: ''
  };
  private defaultConfig: ModalOptions = {
    backdrop: true,
    ignoreBackdropClick: true
  };

  constructor(
    public modalService: BsModalService,
    public commonService: CommonService,
    private translate: TranslateService,
    private toastr: ToastrService,
    public planPeriodsService: PlanPeriodsService,
    public changeSponsorService: ChangeSponsorService
  ) {
    this.companyCodes = COMPANY_CODES;
      this.commonService.setHelpObj({
        header: this.getTranslationWithKey('COR'),
        sections: [],
        lastSavedData: [],
        relatedLinks: [
          {
            label: this.getTranslationWithKey('FLP'),
            url: '/fieldsupport/distributorList/default'
          },
          {
            label: this.getTranslationWithKey('GLY'),
            url: '/genealogyHome/genealogy/default'
          }
        ]
      });
  }

  ngOnInit() {
    this.hasChangeAccess = this.commonService.hasFeatureAccess('Change OrganizationUnit');
    const userInfo = this.commonService.getUserDetails();
    if (userInfo && userInfo.permissions && userInfo.permissions.details) {
      const detailObj = userInfo.permissions.details.find((detailObj: any) => detailObj.code === 'COR');
      if (detailObj != undefined) {
        if (detailObj.code === 'COR') {
          if (detailObj.permission.code === 'RAC') {
            this.isChangeOrgUnitAccess = false;
          }
        }
      } else {
        this.isChangeOrgUnitAccess = false;
      }
    }
    this.companyCode = this.commonService.getCompanyCode();
    this.isOrgUnitChanged = false;
    this.resetValidationErrorMessage();
    this.getPeriodDetails();
    /* MLM-2777 - start */
    const leadershipFunction = this.commonService.getGenealogyConfiguration();
    if (leadershipFunction) {
      this.isCompanyConfigNone = leadershipFunction.name === 'None';
    }
    // VCE - 3549 - "Period Close" check box to be selected By Default
    this.orgUnitPayload.periodClose = true;
  }

  public getTranslationWithKey(key: string) {
    return this.commonService.getTranslatedVal(key);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['organization'] && changes['organization'].currentValue) {
      this.defaultSearchText = changes['organization'].currentValue?.clientId;
      this.fromOrganizationSelected(changes['organization'].currentValue);
      // show movedownlineleg only for Leaders(BM,RM,TL Confirmed(Occasional))
      if (changes['organization'].currentValue.rank > 194) {
        this.showDownlineLeg = true;
        this.orgUnitPayload.moveEntireDownlineLeg = false;
      } else {
        this.orgUnitPayload.moveEntireDownlineLeg = false;
      }
    }
  }

  private getPeriodDetails() {
    this.planPeriodsService
      .getPeriod()
      .then(this.getPeriodDetailsSH.bind(this), (reason) => this.commonService.commonErrorHandler(reason));
  }

  private getPeriodDetailsSH(response: any) {
    this.periodDetails = response;
    if (this.periodDetails) {
      this.checkPeriodClose();
    }
  }

  public checkPeriodClose() {
    if (this.orgUnitPayload.periodClose) {
      this.startPeriodDateInMillis = moment(
        this.periodDetails.currentPeriod && this.periodDetails.currentPeriod.endDate
      )
        .add(1, 'days')
        .valueOf();
      if(['TMES', 'KSES'].includes(this.companyCode)) {
        this.orgUnitPayload.effectiveDate = this.commonService.prepareBSEffectiveDateObj(this.startPeriodDateInMillis);
      } else {
        this.orgUnitPayload.effectiveDate = this.commonService.prepareBSDateObj(this.startPeriodDateInMillis);
      }
    } else {      
      // VCE-26904 - Effective Date should be Period Start Date instead of the current date if Period close not selected
      if (this.periodDetails.currentPeriod && this.periodDetails.currentPeriod.startDate) {
        this.periodStartDateInMillis = moment(this.periodDetails.currentPeriod.startDate);
        // this.orgUnitPayload.effectiveDate = this.commonService.prepareBSDateObj(this.periodStartDateInMillis);
        if(['TMES', 'KSES'].includes(this.companyCode)) {
          this.orgUnitPayload.effectiveDate = this.commonService.prepareBSDateObj(this.currentDate.getTime());
        } else {
          this.orgUnitPayload.effectiveDate = this.commonService.prepareBSDateObj(this.periodStartDateInMillis);
        }
      }
    }
  }

  private resetValidationErrorMessage() {
    this.validationObj = {
      userMessage: '',
      state: ''
    };
  }

  ngAfterViewInit() {
    this.orgUnitForm?.control.valueChanges.subscribe(() => {
      if (this.orgUnitForm?.form?.invalid) {
        this.hasRequiredError = this.commonService.hasRequiredError(this.orgUnitForm?.form?.controls);
      } else {
        this.isFormSubmitted = false;
        this.hasRequiredError = false;
      }
    });
  }

  public fromOrganizationSelected(event: any) {
    this.orgUnitPayload.fromOrgUnit = event;
    this.orgUnitIndex = event.orgUnitIndex;
    // show movedownlineleg only for Leaders(BM,RM,TL Confirmed(Occasional))
    if (this.orgUnitPayload.fromOrgUnit.pinnedAs && this.orgUnitPayload.fromOrgUnit.pinnedAs.rank > 194) {
      this.showDownlineLeg = true;
    }
    this.organizationTypeChange();
  }

  public toOrganizationSelected(event: any) {
    this.orgUnitPayload.toOrgUnit = event;
    if (this.orgUnitPayload.toOrgUnit !== undefined) {
      this.newOrganization = this.orgUnitPayload.toOrgUnit.organizationalUnit;
      this.setFullName(this.newOrganization);
    }
  }

  clearToOrgStructureFilter(event?: any) {
    this.orgUnitPayload.toOrgUnit = null;
  }

  private setEffectiveDate() {
    this.orgUnitPayload.effectiveDate = this.commonService.prepareBSDateObj(new Date().getTime());
  }

  public organizationTypeChange() {
    if (this.orgUnitPayload.fromOrgUnit !== undefined && this.orgUnitPayload.fromOrgUnit.organizationalUnit) {
      this.currentOrganization = this.orgUnitPayload.fromOrgUnit.organizationalUnit;
    } else {
      this.currentOrganization = this.orgUnitPayload.fromOrgUnit.organizationDetails[this.orgUnitIndex];
    }
    this.setFullName(this.currentOrganization);
  }

  public confimationPopUp() {
    const form = this.orgUnitForm?.form;
    let organizationUnit: any;
    this.isFormSubmitted = true;
    this.hasRequiredError = false;
    if (form?.invalid || !this.hasValidFormData()) {
      this.hasRequiredError = this.commonService.hasRequiredError(form?.controls) || !this.hasValidFormData();
    } else {
      this.isFormSubmitted = false;
      this.isOrgUnitChanged = false;
      if (this.orgUnitPayload.fromOrgUnit && this.orgUnitPayload.fromOrgUnit.organizationalUnit) {
        organizationUnit = this.orgUnitPayload.fromOrgUnit.organizationalUnit;
      } else {
        organizationUnit = this.orgUnitPayload.fromOrgUnit.organizationDetails[this.orgUnitIndex];
      }
      this.setFullName(organizationUnit);
      this.setFullName(this.orgUnitPayload.toOrgUnit.organizationalUnit);
      if (this.orgUnitPayload.fromOrgUnit) {
        const firstName = organizationUnit.firstName || '',
          lastName = organizationUnit.lastName || '';
        this.fromName.name = `${firstName} ${lastName}`;
      }
      this.organizationData['fromName'] = this.fromName.name;
      this.organizationData['orgUnitLbl'] = this.getTranslationWithKey('organizational_unit');
      this.organizationData['currentOrgUnitName'] = organizationUnit.orgUnitName;
      if (organizationUnit && organizationUnit.parentOrganizationalUnit) {
        this.organizationData['parentOrgUnitName'] = organizationUnit.parentOrganizationalUnit.orgUnitName;
      } else {
        this.organizationData['parentOrgUnitName'] = organizationUnit.orgUnitName;
      }
      this.organizationData['currentOrganization'] = this.currentOrganization;
      this.organizationData['newOrganization'] = this.newOrganization;
      const initialState = {
        context:{ 
          isFrom: 'OrganizationChange',
          data: this.organizationData}
      };
      
      const modalRef: BsModalRef = this.modalService.show(ChangeOrganizationConfimationModalComponent, {
        ...this.defaultConfig,
        initialState,
        class: 'modal-dialog change-sponsor-reg-ter-modal' // Optional: Adjust modal size as needed
      });
      
      // Handle modal close event
      modalRef.content?.closeModal.subscribe((result: any) => {
        if (result) {
          this.changeOrganization(result);
        }
      });
    }
  }

  public changeOrganization(result: any) {
    let effectiveDate: any;
    if (this.orgUnitPayload.periodClose) {
      effectiveDate = this.startPeriodDateInMillis;
    } else {
      effectiveDate = this.periodStartDateInMillis;
    }
    if (result === 'confirmed') {
      const payload = {
        comments: this.orgUnitPayload.comments,
        effectiveDate: effectiveDate,
        periodClose: !!this.orgUnitPayload.periodClose,
        moveEntireDownlineLeg: !!this.orgUnitPayload.moveEntireDownlineLeg
      };
      this.changeSponsorService
        .changeOrganization(
          payload,
          this.orgUnitPayload.fromOrgUnit?.clientId,
          this.orgUnitPayload.toOrgUnit.organizationalUnit.orgUnitId
        )
        .then(this.successHandler.bind(this), (reason) => this.commonService.commonErrorHandler(reason));
    }
  }

  private successHandler(result: any) {
    if (this.orgUnitPayload.periodClose) {
      this.orgUnitPayload.toOrgUnit = this.orgUnitPayload.toOrgUnit;
    } else {
      this.orgUnitPayload.toOrgUnit = result;
    }
    this.commonService.setUpdateChangeOrgUnitList(result);
    this.resetValidationErrorMessage();
    this.isOrgUnitChanged = true;
    this.toastr.success(this.getTranslationWithKey('operation_success'), this.getTranslationWithKey('success'));
  }

  private hasValidFormData() {
    this.organizationSame = false;
    let organization: any;
    if (this.orgUnitPayload.fromOrgUnit && this.orgUnitPayload.fromOrgUnit.organizationalUnit) {
      organization = this.orgUnitPayload.fromOrgUnit.organizationalUnit;
    } else {
      organization = this.orgUnitPayload.fromOrgUnit.organizationDetails[this.orgUnitIndex];
    }
    const cond =
      organization &&
      this.orgUnitPayload.toOrgUnit &&
      organization.orgUnitId !== this.orgUnitPayload.toOrgUnit.organizationalUnit.orgUnitId;
    if (
      organization &&
      this.orgUnitPayload.toOrgUnit &&
      organization.orgUnitId == this.orgUnitPayload.toOrgUnit.organizationalUnit.orgUnitId
    ) {
      this.organizationSame = true;
    }
    return cond;
  }

  private setFullName(organization: any) {
    if (organization) {
      const firstName = organization.orgUnitName || '';
      organization.name = `${firstName}`;
    }
  }

  public close() {
    this.isOrgUnitChanged = false;
    this.closeSlider.emit();
  }

  public clearOrgStructureFilter() {
    const effDate = this.orgUnitPayload.effectiveDate;
    this.currentOrganization = null;
    this.orgUnitPayload = {};
    this.orgUnitPayload.effectiveDate = effDate;
    // VCE - 3549 - "Period Close" check box to be selected By Default
    this.orgUnitPayload.periodClose = true;
    this.showDownlineLeg = false;
    this.orgUnitPayload.moveEntireDownlineLeg = true;
    this.checkPeriodClose();
  }

  titleCommentsValidation(e: any) {
    this.titleMaxLimit = e.target.value.length == 100;
  }
}
